import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Grid } from '@mui/material';
import type { Options } from 'highcharts';
import { useTranslation } from 'react-i18next';
import ChartCard from '@components/charts/ChartCard';
import { testService, type ScanRunListItem } from '@services/testService';
import { runDensity } from '@utils/runDensity';
import { useFormat } from '@utils/format';

/** The API's newest first, so this is the latest runs: enough to read a direction. */
const RUN_LIMIT = 50;

interface DensityTrendCardProps {
  orgId: string;
  contextId: string;
}

/**
 * Findings per page across a tracked place's runs (REV-141). The trend panel
 * beside it counts findings open now; this asks whether each run found fewer per
 * page than the last, which a growing scope would otherwise hide.
 *
 * One series per source, because a one-page CI run and a whole-site crawl look
 * at different pages: joined into one line they would zigzag with no change in
 * the site at all.
 */
export default function DensityTrendCard({ orgId, contextId }: DensityTrendCardProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const [runs, setRuns] = useState<ScanRunListItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRuns(null);
    setFailed(false);
    void (async () => {
      try {
        const data = await testService.listScanRuns({
          organizationId: orgId,
          contextId,
          status: 'completed',
          limit: RUN_LIMIT,
        });
        if (!cancelled) setRuns(data.items ?? []);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, contextId]);

  const points = useMemo(
    () =>
      (runs ?? []).flatMap((run) => {
        const density = runDensity(run);
        return density === null ? [] : [{ run, density }];
      }),
    [runs]
  );
  const oldestFirst = useMemo(() => [...points].reverse(), [points]);

  if (failed) {
    return (
      <Grid item xs={12}>
        <Alert severity="warning" role="status">
          {t('trackedPlaces.density.error')}
        </Alert>
      </Grid>
    );
  }
  if (!runs) {
    return (
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} aria-label={t('common.loading', 'Loading...')} />
        </Box>
      </Grid>
    );
  }

  const sourceLabel = (scanType: string) =>
    t(`trackedPlaces.history.sourceTypes.${scanType}`, { defaultValue: scanType });
  const whenOf = (run: ScanRunListItem) =>
    format.date(run.completedAt ?? run.createdAt, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  const sources = [...new Set(oldestFirst.map((p) => p.run.scanType))];
  const options: Options | null = oldestFirst.length
    ? {
        chart: { type: 'line', height: 300 },
        title: { text: undefined },
        xAxis: { categories: oldestFirst.map((p) => whenOf(p.run)) },
        yAxis: { title: { text: t('trackedPlaces.density.axis') }, min: 0 },
        legend: { enabled: sources.length > 1 },
        series: sources.map((source) => ({
          type: 'line' as const,
          name: sourceLabel(source),
          connectNulls: true,
          data: oldestFirst.map((p) => (p.run.scanType === source ? p.density : null)),
        })),
      }
    : null;
  const latest = points[0]?.density ?? 0;

  return (
    <ChartCard
      fullWidth
      title={t('trackedPlaces.density.title')}
      description={t('trackedPlaces.density.description', {
        count: oldestFirst.length,
        latest: format.number(latest, { maximumFractionDigits: 1 }),
      })}
      options={options}
      hasData={oldestFirst.length > 0}
      emptyText={t('trackedPlaces.density.empty')}
      tableLabel={t('trackedPlaces.density.tableToggle')}
      columns={[
        t('trackedPlaces.density.colDate'),
        t('trackedPlaces.density.colSource'),
        t('trackedPlaces.density.colPages'),
        t('trackedPlaces.density.colFindings'),
        t('trackedPlaces.density.colDensity'),
      ]}
      rows={oldestFirst.map(({ run, density }) => ({
        to: `/tests/${run.id}`,
        cells: [
          whenOf(run),
          sourceLabel(run.scanType),
          format.number(run.totalTargets),
          format.number(run.totalFindings),
          format.number(density, { maximumFractionDigits: 1 }),
        ],
      }))}
    />
  );
}
